/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
    getSearch,
    hasFiltering,
    makeFilteredItemsSelector,
    makeItemsTagsSelector,
    numberOfActiveFilterSelector,
} from '../../selectors/finder';
import { getAEMResource, getHomepage } from '../../selectors/generic';
import { initializeProducts } from '../../actions/products';
import { initializePromotions } from '../../actions/promotions';
import { fetchTags } from '../../actions/aem';
import { activateFinder, setSearch } from '../../actions/finder';
import { ProductType } from '../../data/products.model';
import { ProductFinderType } from '../../data/finders.model';
import ProductGridCard from '../molecules/cards/ProductGridCard';
import { Finder } from '../templates/Finder';
import { PromotionalBanner } from '../molecules/promotions/PromotionalBanner';
import { browserHistory } from '../../config/redux.store';
import { ConnectedRouter } from 'connected-react-router';
import { areTagLoaded } from '../../selectors/tags';
import { GirdListCtaCard } from '../molecules/cards/GirdListCtaCard';
import {
    areProductsLoading,
    getProductTileData,
    makeProductSearchFilter,
    matchProductWithSearch,
    sortedProductsSelector,
} from '../../selectors/products';
import { HomepageModel } from '../../data/aem.model';
import { FinderTypeAhead } from '../organisms/finders/FinderTypeAhead';
import { TagReferenceBanner } from '../organisms/finders/TagReferenceBanner';
import { OrderBeforeMessage } from '../molecules/finder/OrderBeforeMessage';
import { ShowHide } from '../atoms/layout/ShowHide';
import MyPuratosArea from '../molecules/ecom/MyPuratosArea';
import Alerts from '../molecules/alerts/Alerts';
import * as ReactDOM from 'react-dom';
import { isLoggedIn } from '../../data/user.model';
import { GTMContextProvider } from '../../analytics/ga4/utils/GTMContext';
import { SkeletonTypes } from '../atoms/effects/skeletons/utils/SkeletonTypes';
import { addProductClickEvent } from '../../utils/customEventHandler';
import { GA4InteractionType } from '../../analytics/ga4/types/interaction';
import {
    selectMyPuratosAccountEmail,
    selectUserId,
} from '../../selectors/auth';
import { getCurrencyCode } from '../../selectors/cart';
import { GA4LoginType } from '../../analytics/ga4/types/login';
import { GA4PageType } from '../../analytics/ga4/types/page';
import { googleEvents } from '../../utils/googleEvents';
import { getLanguageIso } from '../../selectors/generic';
import { productEventParseSelect } from '../../analytics/ga4/parsers/item';

export class ProductFinder extends React.Component {
    static defaultProps = {
        resource: {},
    };

    static propTypes = {
        products: PropTypes.arrayOf(PropTypes.shape(ProductType)),
        searchedProducts: PropTypes.arrayOf(PropTypes.shape(ProductType)),
        resource: PropTypes.shape(ProductFinderType),
        homepageResource: PropTypes.shape(HomepageModel),
        numberOfFilter: PropTypes.number,
        hasFiltering: PropTypes.bool,
        tagsLoaded: PropTypes.bool,
        productsLoading: PropTypes.bool,
        search: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape(ProductType),
        ]),
    };

    sortingOptions = [];

    constructor(props) {
        super(props);
        this.state = { search: '' };
        this.renderSubheader = this.renderSubheader.bind(this);
    }

    componentDidMount() {
        const {
            model,
            fetchTags,
            homepageResource,
            initializeProducts,
        } = this.props;

        initializeProducts({ model });
        fetchTags();

        if (homepageResource && Object.entries(homepageResource).length > 0) {
            this.initializeFromHomeProperties();
        }
    }

    componentDidUpdate(oldProps) {
        if (
            (oldProps.tagsLoaded !== true &&
                this.props.tagsLoaded === true &&
                Object.keys(this.props.resource).length > 0) ||
            (this.props.tagsLoaded === true &&
                oldProps.resource !== this.props.resource)
        ) {
            this.props.activateFinder({
                type: 'product',
                filters: this.props.resource.filters,
            });
        }

        if (
            oldProps.homepageResource !== this.props.homepageResource &&
            Object.entries(this.props.homepageResource).length > 0
        ) {
            this.initializeFromHomeProperties();
        }

        if (this.props.alerts !== oldProps.alerts) {
            ReactDOM.findDOMNode(this).scrollIntoView({ behavior: 'smooth' });
        }
    }

    initializeFromHomeProperties() {
        const { homepageResource, initializePromotions } = this.props;

        this.sortingOptions = [
            { label: 'Sort by relevance', value: 'weight' },
            { label: 'Sort by date', value: 'referenceDate' },
            { label: 'Sort alphabetically', value: 'referenceTitle' },
        ];

        if (homepageResource.enableEcommerce) {
            if (isLoggedIn()) {
                initializePromotions();
            }
            this.sortingOptions.push({
                label: 'Sort by promotions first',
                value: 'promotions',
            });
        }
    }

    onClickProduct = (product, position) => {
        const filteredItem = this.props.products.filter(
            (item) => item.promotion?.sku === product[0].materialNumber
        );

        addProductClickEvent(
            product,
            GA4InteractionType.allProducts,
            false,
            this.props.userId,
            this.props.currency,
            position
        );
        googleEvents.selectItem({
            user_id: this.props.userId,
            page_language: getLanguageIso(),
            email_address: isLoggedIn() ? this.props.userEmail : '',
            login_status: this.props.userId
                ? GA4LoginType.LOGGED_IN
                : GA4LoginType.GUEST,
            website_section: GA4PageType.allProducts,
            items: productEventParseSelect(
                product,
                this.props.currency,
                filteredItem[0]?.promotion || {},
                GA4InteractionType.allProducts
            ),
        });
    };

    renderCard = (props) => {
        const { homepageResource, resource, productTileData } = this.props;

        return (
            <GTMContextProvider type={GA4InteractionType.allProducts}>
                <ProductGridCard
                    {...props}
                    isFinder
                    hoverEffect={
                        resource && resource.hoverEffectDeactivated !== true
                    }
                    showReferenceImageFirst={
                        resource && resource.showReferenceImageFirst
                    }
                    fallback={
                        homepageResource && homepageResource.fallbackImage
                    }
                    allowedTags={resource && resource.resultsTag}
                    showPlantForwardIcons={true}
                    addEvent={this.onClickProduct}
                    tileData={
                        productTileData?.filter(
                            (tile) => tile.aemProductId === props?.item?.id
                        )[0] || []
                    }
                />
            </GTMContextProvider>
        );
    };

    renderSubheader() {
        if (!this.props.resource) {
            return null;
        }

        const { resource, numberOfFilter, hasFiltering } = this.props;
        const { thematicTitle, highlight1, highlight2 } = resource || {};

        return (
            <React.Fragment>
                <MyPuratosArea requireEcom requireOrderBeforeMessage>
                    <OrderBeforeMessage key={'order-before'} />
                </MyPuratosArea>
                <ShowHide show={!hasFiltering && (highlight1 || highlight2)}>
                    <PromotionalBanner
                        thematicTitle={thematicTitle}
                        highlight1={highlight1}
                        highlight2={highlight2}
                    />
                </ShowHide>
                <ShowHide show={numberOfFilter === 1}>
                    <TagReferenceBanner />
                </ShowHide>
                <Alerts marginTop marginBottom={false} />
            </React.Fragment>
        );
    }

    render() {
        const {
            resource,
            hasFiltering,
            products,
            searchedProducts,
            productsLoading,
            search,
            setSearch,
            availableTags,
        } = this.props;

        return (
            <ConnectedRouter history={browserHistory}>
                <Finder
                    resource={resource}
                    items={searchedProducts}
                    hasFiltering={hasFiltering}
                    card={this.renderCard}
                    availableTags={availableTags}
                    ctaCard={
                        resource.ctacard && (
                            <GirdListCtaCard {...resource.ctacard} />
                        )
                    }
                    loading={productsLoading}
                    skeletonType={SkeletonTypes.PRODUCT_FINDER}
                    sortingOptions={this.sortingOptions}
                    renderSubheader={this.renderSubheader}
                    showPricingOnCards
                    search={
                        <FinderTypeAhead
                            key="typeahead"
                            emptyLabel={resource.noResultsMessage || ''}
                            items={products}
                            search={
                                search && search.startsWith('searchid:')
                                    ? searchedProducts[0]
                                        ? searchedProducts[0].title
                                        : '?'
                                    : search
                            }
                            placeholder={resource.searchBarPlaceholder}
                            filter={(option, props) =>
                                matchProductWithSearch(option, props.text)
                            }
                            setSearch={setSearch}
                        />
                    }
                />
            </ConnectedRouter>
        );
    }
}

const mapStateToProps = () => {
    const availableTagsSelector = makeItemsTagsSelector(sortedProductsSelector);
    const productSelector = makeFilteredItemsSelector(sortedProductsSelector);
    const searchedItems = makeProductSearchFilter(productSelector, getSearch);

    return (state, ownProps) => ({
        resource: getAEMResource(state, ownProps.model),
        homepageResource: getHomepage(state),
        products: productSelector(state),
        searchedProducts: searchedItems(state),
        hasFiltering: hasFiltering(state),
        numberOfFilter: numberOfActiveFilterSelector(state),
        tagsLoaded: areTagLoaded(state),
        productsLoading: areProductsLoading(state),
        search: getSearch(state),
        availableTags: availableTagsSelector(state),
        alerts: state.alerts.alerts,
        productTileData: getProductTileData(state),
        userId: selectUserId(state),
        currency: getCurrencyCode(state),
        userEmail: selectMyPuratosAccountEmail(state),
    });
};

const mapActionToProps = {
    initializeProducts,
    initializePromotions,
    fetchTags,
    activateFinder,
    setSearch,
};

export default connect(mapStateToProps, mapActionToProps)(ProductFinder);
